import { ICoupon, ICouponApplied, ICouponBase } from 'types/coupon';
import getValue from 'lodash/get';
import { getCouponScope, getCouponType } from 'services/coupon/type';
import { couponScopeConst, couponTypeConst } from 'config/coupon';
import { ICampaign } from 'types/campaign';
import findIndex from 'lodash/findIndex';
import union from 'lodash/union';
import intersection from 'lodash/intersection';
import { CartItem, CouponGoodsItem } from 'types/cart';
import {
    computePlatformCouponBaseAmount,
    computeShopCouponBaseAmount
} from 'services/coupon/coupon-for-goods-list';
import { getLiveEventParams } from 'utils/helper';

/**
 * 优惠券是否到达开始时间
 * @param coupon
 */
export function canUseCouponInTime(coupon: ICouponBase) {
    return new Date().getTime() >= new Date(coupon.startTime.replace(/-/g, '/')).getTime();
}

/**
 * 满减优惠券是否能用
 * @param coupon
 * @param amount
 */
function canUseFullReducedCoupon(coupon: ICouponBase, amount: number) {
    return amount >= Number(coupon.threshold);
}

/**
 * 优惠券是否达到使用价格
 * @param coupon
 * @param amount
 */
function canUseCouponInPrice(coupon: ICouponBase, amount: number) {
    const couponType = getCouponType(coupon);

    if (couponType === couponTypeConst.fullDiscount) {
        return canUseFullReducedCoupon(coupon, amount);
    }
    if (couponType === couponTypeConst.discount) {
        return amount > 1;
    }

    return true;
}

export const getUnavailableCoupon = (
    couponList: Array<ICouponApplied>,
    shopId: string[],
    supplyStatus: { shopId: string; supplyStatus: number }[],
    payType: number[],
    productId: string[],
    campaign?: (ICampaign | undefined)[]
) => {
    if (!Array.isArray(couponList) || couponList.length === 0) {
        return [];
    }
    const liveEventParams = getLiveEventParams();

    // eslint-disable-next-line complexity
    return couponList.filter((coupon) => {
        const unavailableReason = [];
        coupon.explain = [];

        if (
            getCouponScope(coupon) === couponScopeConst.shop &&
            coupon.shopId !== 'fUCPXX' &&
            supplyStatus.filter((item) => item.shopId === coupon.shopId).length > 0
        ) {
            if (
                supplyStatus.filter(
                    (item) => item.supplyStatus !== 10 && item.shopId === coupon.shopId
                ).length === 0
            ) {
                unavailableReason.push('特殊商品不能使用店铺券');
            }
        }

        if (!canUseCouponInTime(coupon)) {
            unavailableReason.push(`在${coupon.startTime}后可使用`);
        }

        // 如果优惠券是 直播券（kolId），则需要判断当前是通过直播间下单，并且当前主播（live_author_id） === kolId，才可以使用
        if (coupon.kol_scope_tag === 1) {
            if (
                !getValue(liveEventParams, 'live_room_id') ||
                getValue(liveEventParams, 'live_author_id') !== coupon.kolUid
            ) {
                unavailableReason.push('指定直播间可用');
            }
        }

        // 指定店铺的平台券
        if (
            getCouponScope(coupon) === couponScopeConst.platform &&
            coupon.platformSubType === 1 &&
            coupon.typeDetail
        ) {
            const availableShopList = coupon.typeDetail.split(',');
            if (
                union(availableShopList, shopId).length ===
                availableShopList.length + shopId.length
            ) {
                unavailableReason.push('非可用商家');
            }
        }

        // 指定商品的平台券
        // TODO: 购物车指定商品临时方案，平台指定商品优惠券目前不支持购物车
        if (getCouponScope(coupon) === couponScopeConst.platform && coupon.platformSubType === 2) {
            // @ts-ignore
            if (window['is_cart_page']) {
                unavailableReason.push('购物车下单不支持使用');
            }
        }

        // 指定店铺的平台券
        if (
            getCouponScope(coupon) === couponScopeConst.shop &&
            coupon.platformSubType === 1 &&
            coupon.typeDetail &&
            Math.floor(coupon.type / 10) === 4
        ) {
            const availableProductList = coupon.typeDetail.split(',');
            if (
                union(availableProductList, productId).length ===
                new Set(availableProductList).size + new Set(productId).size
            ) {
                unavailableReason.push('所结算商品不符合使用条件');
            }
        }

        if (
            campaign &&
            campaign.length > 0 &&
            campaign.filter((item) => !(item && !item.canUseCoupon && !item.isPre)).length === 0
        ) {
            unavailableReason.push('与秒杀价格不能共享');
        }

        if (
            payType.filter((item) => item === 0).length === payType.length &&
            getCouponScope(coupon) === couponScopeConst.platform
        ) {
            unavailableReason.push('仅支持在线支付商品使用');
        }

        if (unavailableReason.length) {
            coupon.explain = unavailableReason;
            return true;
        }
        return false;
    });
};

export const getAvailableCoupon = (
    couponList: Array<ICouponApplied>,
    shopId: string[],
    supplyStatus: { shopId: string; supplyStatus: number }[],
    payType: number[],
    productId: string[],
    campaign?: (ICampaign | undefined)[]
) => {
    if (!Array.isArray(couponList) || couponList.length === 0) {
        return [];
    }
    const liveEventParams = getLiveEventParams();

    // eslint-disable-next-line complexity
    return couponList.filter((coupon) => {
        if (
            getCouponScope(coupon) === couponScopeConst.shop &&
            coupon.shopId !== 'fUCPXX' &&
            supplyStatus.filter((item) => item.shopId === coupon.shopId).length > 0
        ) {
            if (
                supplyStatus.filter(
                    (item) => item.supplyStatus !== 10 && item.shopId === coupon.shopId
                ).length === 0
            ) {
                return false;
            }
        }

        if (!canUseCouponInTime(coupon)) {
            return false;
        }

        // 如果优惠券是 直播券（kolId !== ''），则需要判断当前是通过直播间下单，并且当前主播（live_author_id） === kolId，才可以使用
        if (coupon.kol_scope_tag === 1) {
            if (
                !getValue(liveEventParams, 'live_room_id') ||
                getValue(liveEventParams, 'live_author_id') !== coupon.kolUid
            ) {
                return false;
            }
        }

        // 指定店铺的平台券
        if (
            getCouponScope(coupon) === couponScopeConst.platform &&
            coupon.platformSubType === 1 &&
            coupon.typeDetail
        ) {
            const availableShopList = coupon.typeDetail.split(',');
            if (
                union(availableShopList, shopId).length ===
                availableShopList.length + shopId.length
            ) {
                return false;
            }
        }

        // 指定商品的平台券
        // TODO: 购物车指定商品临时方案，平台指定商品优惠券目前不支持购物车
        if (getCouponScope(coupon) === couponScopeConst.platform && coupon.platformSubType === 2) {
            // @ts-ignore
            if (window['is_cart_page']) {
                return false;
            }
        }

        // 指定店铺的平台券
        if (
            getCouponScope(coupon) === couponScopeConst.shop &&
            coupon.platformSubType === 1 &&
            coupon.typeDetail &&
            Math.floor(coupon.type / 10) === 4
        ) {
            const availableProductList = coupon.typeDetail.split(',');
            if (
                union(availableProductList, productId).length ===
                new Set(availableProductList).size + new Set(productId).size
            ) {
                return false;
            }
        }

        if (
            campaign &&
            campaign.length > 0 &&
            campaign.filter((item) => !(item && !item.canUseCoupon && !item.isPre)).length === 0
        ) {
            return false;
        }

        return !(
            payType.filter((item) => item === 0).length === payType.length &&
            getCouponScope(coupon) === couponScopeConst.platform
        );
    });
};

export const getShopCoupon = (couponList: Array<ICouponApplied>, goodsList: CouponGoodsItem[]) => {
    if (!Array.isArray(couponList) || couponList.length === 0) {
        return [];
    }

    return couponList.filter((coupon) => {
        const couponScope = getCouponScope(coupon);

        if (couponScope !== couponScopeConst.shop) {
            return false;
        }
        const unavailableReason = [];
        coupon.explain = coupon.explain || [];
        let newGoodsList: CouponGoodsItem[] = [];

        if (Math.floor(coupon.type / 10) === 2) {
            newGoodsList = goodsList.map((goodsItem) => {
                return { ...goodsItem };
            });
        } else {
            const ids = coupon.typeDetail.split(',');
            if (coupon.typeDetail && ids.length > 0) {
                newGoodsList = goodsList
                    .filter((item) => ids.indexOf(item.id) !== -1)
                    .map((goodsItem) => {
                        return { ...goodsItem };
                    });
            }
        }

        const len = newGoodsList.length;
        if (len > 0) {
            let j = 0;
            for (j; j < len; j++) {
                const goods = newGoodsList[j];
                if (!goods.isLimit) {
                    break;
                }
            }
            if (j === len) {
                unavailableReason.push('无该店铺商品参与结算');
            }

            let i = 0;
            for (i; i < len; i++) {
                const goods = newGoodsList[i];
                if (
                    !(goods.FDReducer > 0 && goods.fullDiscount && !goods.fullDiscount.canUseCoupon)
                ) {
                    break;
                }
            }
            if (i === len) {
                unavailableReason.push('与满减促销不能共享');
            }
        } else {
            unavailableReason.push('所结算商品不符合使用条件');
        }

        const { base } = computeShopCouponBaseAmount(newGoodsList, coupon);
        if (!canUseCouponInPrice(coupon, base)) {
            unavailableReason.push(`差${(coupon.threshold - base) / 100}元可用`);
        }

        if (unavailableReason.length) {
            coupon.explain = unavailableReason;
            return false;
        }

        return true;
    });
};

export const getPlatformCoupon = (
    couponList: Array<ICouponApplied>,
    goodsList: CouponGoodsItem[]
) => {
    if (!Array.isArray(couponList) || couponList.length === 0) {
        return [];
    }

    return couponList.filter((coupon) => {
        const couponScope = getCouponScope(coupon);
        if (couponScope !== couponScopeConst.platform) {
            return false;
        }
        const unavailableReason = [];
        coupon.explain = coupon.explain || [];

        let newGoodsList: CouponGoodsItem[] = goodsList.map((goodsItem) => {
            return { ...goodsItem };
        });

        if (coupon.typeDetail && coupon.typeDetail.split(',').length > 0) {
            const shopIds = coupon.typeDetail.split(',');
            const shopLen = shopIds.length;
            let flag = false;
            for (let i = 0; i < shopLen; i++) {
                const shopId = shopIds[i];
                const shopGoods = newGoodsList.filter(
                    (item) => item.shopId === shopId && !item.isLimit
                );
                if (shopGoods.length > 0) {
                    flag = true;
                    break;
                }
            }

            if (!flag) {
                unavailableReason.push('无该店铺商品参与结算');
            }
        } else {
            if (newGoodsList.filter((item) => item.isLimit).length === newGoodsList.length) {
                unavailableReason.push('无该店铺商品参与结算');
            }
        }

        const { base } = computePlatformCouponBaseAmount(newGoodsList, coupon);
        if (!canUseCouponInPrice(coupon, base)) {
            unavailableReason.push(`差${(coupon.threshold - base) / 100}元可用`);
        }

        if (unavailableReason.length) {
            coupon.explain = unavailableReason;
            return false;
        }

        return true;
    });
};

/*
 * 店铺券的选择变化会引起平台券的能否选择变化
 * 店铺券由选择变成取消： 满减平台券不满足的可能变成满足
 * 店铺券由取消变成勾选： 满减平台券原本满足的变成不满足，如果这张券恰好勾选要变成不够选，且当前与其互斥的券变成可选状态
 **/
export const recheckPlatformCoupon = (couponList: Array<ICouponApplied>, baseAmount: number) => {
    if (!Array.isArray(couponList) || couponList.length === 0) {
        return [];
    }

    return couponList.filter((coupon) => {
        const couponScope = getCouponScope(coupon);
        if (couponScope !== couponScopeConst.platform) {
            return false;
        }
        if (!coupon.explain) {
            coupon.explain = [];
        }

        const index = findIndex(coupon.explain, (item) => item.indexOf('元可用') > -1);
        if (!canUseCouponInPrice(coupon, baseAmount)) {
            if (index === -1) {
                coupon.explain.push(`差${(coupon.threshold - baseAmount) / 100}元可用`);
            }
        } else {
            if (index > -1) {
                coupon.explain.splice(index, 1);
            }
        }

        if (coupon.explain.length) {
            return true;
        }

        return false;
    });
};

export const getOtherShopCoupon = (list: ICouponApplied[], shopIds: string[]) => {
    return list.filter((item) => {
        return (
            getCouponScope(item) === couponScopeConst.shop && shopIds.indexOf(item.shopId) === -1
        );
    });
};

export const getSelfShopCoupon = (list: ICouponApplied[], shopIds: string[]) => {
    return list.filter((item) => {
        return (
            getCouponScope(item) === couponScopeConst.platform || shopIds.indexOf(item.shopId) > -1
        );
    });
};
